import type { ShapeType } from '../../AppearanceDefinitions';
import { BODY_RIGS, type Point } from '../HumanoidGeometry';
import { createCircle, createPath, darken } from './svgPrims';
import { legacyPainter, type Painter } from './NpcFinish';
import { renderNpcProps } from './NpcProps';
import type { HumanoidExtras, IdlePhase } from './HumanoidTypes';

export interface LayeredContent {
  /** Behind the body (cloak drape, slung bow, quiver). */
  back: string;
  /** Over the body, under the face (tunics, robes, aprons, mantles). */
  clothing: string;
  /** Between eyes and mouth (beards — mouth draws on top). */
  beard: string;
  /** Over the face (hair, hats, hoods). */
  head: string;
  /** Topmost (canes, satchels, lanterns). */
  front: string;
  /** Over the clothing and under the face, never clipped to the body (nets). */
  worn: string;
}

export const EMPTY_LAYERS: LayeredContent = { back: '', clothing: '', beard: '', head: '', front: '', worn: '' };

export interface ExtrasContext {
  painter?: Painter;
  phase?: IdlePhase;
  frontHand: Point;
  /**
   * Centre the fixed-coordinate garment details (lacing, robe collar, apron
   * panel, clasp) on the body shape instead of the classic body's x=60. Legacy
   * renders keep the historical placement byte-for-byte.
   */
  alignToShape?: boolean;
  /** Plan-12 fit option (`humanoid.fit`): cloth cut to the body outline, props held/worn. */
  fit?: boolean;
  backHand?: Point;
}

type Metrics = { left: number; right: number; centerX: number };

/**
 * Fitted garments (plan 12): cloth is cut wider than the body and clipped to
 * the body silhouette by GhostRenderer, so every edge follows the ghost's
 * curve — the hem rides the scalloped tail, belts and collars wrap the sides.
 */
function renderFittedGarment(extras: HumanoidExtras, m: Metrics, paint: Painter, fixed: (svg: string) => string): string {
  const g = extras.garment;
  if (!g) return '';
  const L = m.left - 16;
  const R = m.right + 16;
  const top = g.kind === 'robe' ? 52 : 54;
  const body = (fill: string) => `<path data-npc-garment="${g.kind}" d="M${L},${top} Q${m.centerX},${top + 12} ${R},${top} L${R},126 L${L},126 Z" fill="${fill}" />`;
  let out = '';
  if (g.kind === 'apron') {
    out += body(paint.fill(g.underColor ?? darken(g.color, 1.2)));
  } else {
    out += body(paint.fill(g.color));
  }
  if (g.trimColor) {
    const hemY = g.kind === 'robe' ? 104 : 99;
    out += `<path data-npc-hem="true" d="M${L},${hemY} Q${m.centerX},${hemY + 8} ${R},${hemY} L${R},126 L${L},126 Z" fill="${g.trimColor}" />`;
  }
  // Fold shadow down the far side sells the round body under the cloth.
  out += `<path d="M${L},${top} L${m.left + 10},${top + 4} Q${m.left + 4},90 ${m.left + 12},126 L${L},126 Z" fill="#000000" opacity="0.14" />`;
  if (g.kind === 'tunic') {
    out += fixed(`<path d="M56,58 L64,64 M64,58 L56,64" stroke="${darken(g.color, 0.7)}" stroke-width="1.5" />`);
  }
  if (g.kind === 'robe') {
    if (g.underColor) out += fixed(createPath('M52,53 L60,67 L68,53 Z', g.underColor));
    if (g.trimColor) out += fixed(`<rect x="58" y="66" width="4" height="42" fill="${g.trimColor}" opacity="0.8" />`);
    out += `<path d="M${L},82 Q${m.centerX},92 ${R},82" stroke="${darken(g.underColor ?? g.color, 0.8)}" stroke-width="2.5" fill="none" />`;
  }
  if (g.kind === 'apron') {
    let apron = `<path d="M48,58 L40,44 M72,58 L80,44" stroke="${g.color}" stroke-width="3.5" stroke-linecap="round" />`;
    apron += createPath('M46,56 L74,56 Q82,80 80,100 Q60,112 40,100 Q38,80 46,56 Z', paint.fill(g.color));
    if (g.pocketBottles) {
      apron += `<rect x="52" y="76" width="5.5" height="9" rx="2" fill="#a04848" />
          <rect x="61" y="75" width="5.5" height="10" rx="2" fill="#4858a0" />`;
    }
    apron += `<rect x="49" y="82" width="22" height="13" rx="2.5" fill="${darken(g.color, 0.78)}" />`;
    out += fixed(apron);
  }
  if (g.beltColor) {
    out += `<path d="M${L},78 Q${m.centerX},${90} ${R},78" stroke="${g.beltColor}" stroke-width="5" fill="none" />`;
    out += fixed(`<rect x="56" y="82" width="8" height="7" rx="1.5" fill="${darken(g.beltColor, 1.6)}" />`);
  }
  return out;
}

function renderGarment(extras: HumanoidExtras, m: Metrics, paint: Painter, fixed: (svg: string) => string): string {
  const g = extras.garment;
  if (!g) return '';
  let out = '';
  if (g.kind === 'tunic') {
    out += createPath(
      `M${m.left + 8},54 Q60,64 ${m.right - 8},54 L${m.right - 4},98 Q${m.centerX},112 ${m.left + 4},98 Z`,
      paint.fill(g.color),
    );
    if (g.trimColor) {
      out += `<path d="M${m.left + 4},98 Q${m.centerX},112 ${m.right - 4},98" stroke="${g.trimColor}" stroke-width="7" fill="none" />`;
    }
    // Simple lace-up detail at the chest.
    out += fixed(`<path d="M56,58 L64,64 M64,58 L56,64" stroke="${darken(g.color, 0.7)}" stroke-width="1.5" />`);
  }
  if (g.kind === 'robe') {
    out += createPath(
      `M${m.left + 6},52 Q60,62 ${m.right - 6},52 L${m.right - 2},106 Q${m.centerX},118 ${m.left + 2},106 Z`,
      paint.fill(g.color),
    );
    if (g.underColor) {
      out += fixed(createPath('M52,53 L60,67 L68,53 Z', g.underColor));
    }
    if (g.trimColor) {
      out += fixed(`<rect x="58" y="66" width="4" height="42" fill="${g.trimColor}" opacity="0.8" />`);
      out += `<path d="M${m.left + 2},104 Q${m.centerX},116 ${m.right - 2},104" stroke="${g.trimColor}" stroke-width="6" fill="none" />`;
    }
    // Cord belt.
    out += `<path d="M${m.left + 10},82 Q${m.centerX},88 ${m.right - 10},82" stroke="${darken(g.underColor ?? g.color, 0.8)}" stroke-width="2.5" fill="none" />`;
  }
  if (g.kind === 'apron') {
    const under = g.underColor ?? darken(g.color, 1.2);
    out += createPath(
      `M${m.left + 8},54 Q60,64 ${m.right - 8},54 L${m.right - 4},98 Q${m.centerX},112 ${m.left + 4},98 Z`,
      paint.fill(under),
    );
    if (g.trimColor) {
      out += `<path d="M${m.left + 4},98 Q${m.centerX},112 ${m.right - 4},98" stroke="${g.trimColor}" stroke-width="8" fill="none" />`;
    }
    // Apron straps + panel + pocket.
    let apron = `<path d="M48,58 L44,47 M72,58 L76,47" stroke="${g.color}" stroke-width="3.5" stroke-linecap="round" />`;
    apron += createPath('M46,56 L74,56 L80,100 Q60,110 40,100 Z', paint.fill(g.color));
    if (g.pocketBottles) {
      apron += `<rect x="52" y="76" width="5.5" height="9" rx="2" fill="#a04848" />
          <rect x="61" y="75" width="5.5" height="10" rx="2" fill="#4858a0" />`;
    }
    apron += `<rect x="49" y="82" width="22" height="13" rx="2.5" fill="${darken(g.color, 0.78)}" />`;
    out += fixed(apron);
  }
  if (g.beltColor) {
    out += `<path d="M${m.left + 7},80 Q${m.centerX},88 ${m.right - 7},80" stroke="${g.beltColor}" stroke-width="5" fill="none" />`;
    out += `<rect x="${m.centerX - 4}" y="81" width="8" height="7" rx="1.5" fill="${darken(g.beltColor, 1.6)}" />`;
  }
  return out;
}

function renderHair(extras: HumanoidExtras): string {
  const h = extras.hair;
  if (!h) return '';
  let out = '';
  switch (h.style) {
  case 'short':
    out += createPath(
      'M30,40 Q28,10 60,7 Q92,10 90,40 Q84,26 74,30 Q66,20 56,28 Q44,22 38,32 Q33,34 30,40 Z',
      h.color,
    );
    break;
  case 'bun':
    out += createCircle(60, 8, 9, h.color);
    out += createPath('M30,44 Q28,8 60,6 Q92,8 90,44 L86,44 Q88,18 60,14 Q32,18 34,44 Z', h.color);
    break;
  case 'loose':
    out += createPath(
      'M30,66 Q26,10 60,7 Q94,10 92,58 Q86,44 84,26 Q74,16 60,16 Q46,16 36,28 Q34,48 30,66 Z',
      h.color,
    );
    out += `<path d="M88,30 Q96,44 90,58" stroke="${h.color}" stroke-width="2.5" fill="none" />`;
    break;
  case 'thin':
    out += createPath('M31,48 Q29,30 37,26 Q35,38 41,48 Q36,52 31,48 Z', h.color);
    out += createPath('M89,48 Q91,30 83,26 Q85,38 79,48 Q84,52 89,48 Z', h.color);
    break;
  }
  if (h.streakColor) {
    out += `<path d="M44,14 Q50,10 55,12 M68,10 Q74,8 80,13" stroke="${h.streakColor}" stroke-width="2" fill="none" stroke-linecap="round" />`;
  }
  return out;
}

function renderHeadgear(extras: HumanoidExtras): string {
  const hg = extras.headgear;
  if (!hg) return '';
  if (hg.kind === 'straw_hat') {
    return `<g transform="rotate(-8 60 21)">
        ${createPath('M36,21 Q38,1 60,1 Q82,1 84,21 Z', darken(hg.color, 0.95))}
        <ellipse cx="60" cy="21" rx="45" ry="9" fill="${hg.color}" />
        <path d="M15,21 A45,9 0 0 0 105,21 Z" fill="${darken(hg.color, 0.88)}" />
        ${hg.accentColor ? `<path d="M38,17 Q38,10 41,9 L79,9 Q82,10 82,17 Q60,22 38,17 Z" fill="${hg.accentColor}" />` : ''}
      </g>`;
  }
  if (hg.kind === 'warden_hat') {
    const feather = hg.accentColor ?? '#d8c8a0';
    return `<g data-headgear="warden_hat" transform="rotate(-6 62 20)">
        <path d="M84,15 Q98,2 108,-6 Q102,6 88,18 Z" fill="${feather}" />
        <path d="M86,16 Q97,5 106,-4" stroke="${darken(feather, 0.7)}" stroke-width="0.9" fill="none" />
        ${createPath('M38,22 Q38,4 52,3 Q60,8 68,3 Q83,4 84,22 Z', hg.color)}
        <path d="M44,8 Q60,13 78,8" stroke="${darken(hg.color, 0.75)}" stroke-width="1.6" fill="none" />
        <path d="M38,17 Q61,22 84,17 L84,22 Q61,27 38,22 Z" fill="${darken(hg.color, 0.6)}" />
        <path d="M18,24 Q60,14 104,22 Q106,26 100,28 Q60,24 22,30 Q16,28 18,24 Z" fill="${darken(hg.color, 0.88)}" />
      </g>`;
  }
  if (hg.kind === 'leaf_crown') {
    const berry = hg.accentColor ?? '#c8483a';
    const leaf = (x: number, y: number, r: number) =>
      `<path d="M${x},${y} Q${x + 5},${y - 7} ${x + 1},${y - 12} Q${x - 4},${y - 6} ${x},${y} Z" fill="${hg.color}" transform="rotate(${r} ${x} ${y})" />`;
    return `<g data-headgear="leaf_crown">
        <path d="M32,24 Q60,12 88,22" stroke="${darken(hg.color, 0.6)}" stroke-width="3" fill="none" />
        ${leaf(36, 24, -40)}${leaf(46, 19, -20)}${leaf(57, 16, 0)}${leaf(68, 16, 15)}${leaf(79, 19, 35)}${leaf(87, 23, 55)}
        ${createCircle(52, 17, 2.2, berry)}${createCircle(74, 17, 2.2, berry)}
      </g>`;
  }
  // Hood: face opening in shadow — watchful glints instead of eyes.
  let out = createPath(
    'M28,62 Q24,20 46,8 Q54,2 62,2 Q80,4 88,14 Q98,26 94,62 Q78,52 60,54 Q42,52 28,62 Z',
    hg.color,
  );
  out += '<ellipse cx="60" cy="42" rx="23" ry="15" fill="#0d1416" opacity="0.85" />';
  out += createCircle(50, 42, 2.5, '#cfe3d8');
  out += createCircle(72, 42, 2.5, '#cfe3d8');
  out += `<path d="M32,58 Q30,22 50,9" stroke="${hg.accentColor ?? darken(hg.color, 1.4)}" stroke-width="2" fill="none" />`;
  return out;
}

export function renderHumanoidExtras(extras: HumanoidExtras, shape: ShapeType, ctx: ExtrasContext): LayeredContent {
  const m = BODY_RIGS[shape]?.silhouette ?? BODY_RIGS.classic.silhouette;
  const paint = ctx.painter ?? legacyPainter;
  const dx = ctx.alignToShape ? m.centerX - 60 : 0;
  const fixed = (svg: string) => (dx === 0 ? svg : `<g transform="translate(${dx} 0)">${svg}</g>`);
  const out: LayeredContent = { back: '', clothing: '', beard: '', head: '', front: '', worn: '' };
  const fit = !!ctx.fit;

  // ── Cloak (drape behind + shoulder mantle w/ clasp in front) ──
  if (extras.cloak) {
    const c = extras.cloak;
    out.back += createPath(
      `M${m.left - 4},46 Q60,28 ${m.right + 4},46 L${m.right + 8},106 Q${m.centerX},121 ${m.left - 8},106 Z`,
      paint.fill(c.color),
    );
    out.back += createPath(
      `M${m.left - 8},106 Q${m.centerX},121 ${m.right + 8},106 L${m.right + 8},110 Q${m.centerX},124 ${m.left - 8},110 Z`,
      darken(c.color, 0.75),
    );
    out.clothing += fit
      ? createPath(`M${m.left - 16},44 Q${m.centerX},62 ${m.right + 16},44 L${m.right + 16},54 Q${m.centerX},72 ${m.left - 16},54 Z`, darken(c.color, 0.88))
      : createPath(
        `M${m.left + 6},48 Q60,62 ${m.right - 6},48 L${m.right - 6},55 Q60,71 ${m.left + 6},55 Z`,
        darken(c.color, 0.88),
      );
    out.clothing += fixed(createCircle(60, 54, 3.5, c.claspColor ?? '#c8b878'));
  }

  out.clothing += fit ? renderFittedGarment(extras, m, paint, fixed) : renderGarment(extras, m, paint, fixed);

  const props = renderNpcProps(extras.props, { m, paint, phase: ctx.phase ?? 0, frontHand: ctx.frontHand, backHand: ctx.backHand ?? ctx.frontHand, fit });
  out.back += props.back;
  out.clothing += props.clothing;
  out.worn += props.worn;

  // ── Beard (drawn under the mouth so the mouth stays readable) ──
  if (extras.beard) {
    const b = extras.beard;
    if (b.style === 'stubble') {
      out.beard += createPath('M42,56 Q42,80 60,83 Q78,80 78,56 Q72,68 60,70 Q48,68 42,56 Z', b.color, 'none', 0, 0.3);
    } else if (b.style === 'short') {
      out.beard += createPath('M42,55 Q42,83 60,87 Q78,83 78,55 Q72,66 60,68 Q48,66 42,55 Z', b.color);
    } else {
      out.beard += createPath('M42,55 Q40,92 60,98 Q80,92 78,55 Q72,66 60,68 Q48,66 42,55 Z', b.color);
    }
  }

  out.head += renderHair(extras);
  out.head += renderHeadgear(extras);
  out.front += props.front;
  return out;
}
